import * as React from 'react';
import NextImage from 'next/image';
import { getCoachPageUrl } from 'constants/pages';
import {
  type EngagementSource,
  withEngagementSource,
} from 'constants/payloads/club/engagementBeacon';
import { EMPTY_COACH_AVATAR_SRC } from 'constants/user';
import { CoachSports, SportsEnum } from 'types/generated/client';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { pluralize } from 'utils/shared/pluralize';
import { useEngagementImpression } from 'hooks/useEngagementImpression';
import Location from 'svg/Location';
import Players from 'svg/Players';
import Price from 'svg/Price';
import Racket from 'svg/Racket';
import Star from 'svg/Star';
import BoltIcon from 'svg/BoltIcon';
import Link from 'components/Link';
import SportBadge from 'components/SportBadge';
import classNames from 'styles/utils/classNames';

interface Props {
  id: string;
  slug: string;
  photoUrl?: string;
  certificationImages?: string[];
  fullName: string;
  price: number;
  isLink?: boolean;
  distance: number | null;
  isBookButton?: boolean;
  followersCount?: number;
  currency: string;
  defaultSport?: string;
  isProfessionalRacquetRegistryActive?: boolean;
  coachSports?: CoachSports[];
  reviews?: {
    averageRating: number;
    totalReviews: number;
  } | null;
  lessonGiven?: number;
  /**
   * @note Could make this optional if there are places where there is not a sport selected or there are multiple sports
   */
  activeSport: SportsEnum;
  /**
   * Whether the coach is eligible for instant booking
   */
  isInstantBookEligible?: boolean;
  /**
   * Whether the coach is a PCI member
   */
  isPciMember?: boolean;
  /**
   * Whether the coach is a VAIR certified rater
   */
  isVairPro?: boolean;
  /** When set, fires a COACH_PROFILE impression with this source (opt-in for discovery surfaces). */
  impressionSource?: EngagementSource;
}

const isNotNullOrUndefined = (value: number | undefined): value is number => {
  return value !== undefined && value >= 0;
};

const Content = ({
  photoUrl,
  fullName,
  price,
  distance,
  isBookButton = false,
  followersCount,
  currency,
  isProfessionalRacquetRegistryActive,
  coachSports,
  lessonGiven,
  reviews,
  defaultSport,
  activeSport,
  isInstantBookEligible = false,
  isPciMember,
  isVairPro,
  containerRef,
}: Props & { containerRef?: React.Ref<HTMLDivElement> }) => {
  const currentSport =
    activeSport ||
    coachSports?.filter((coachSport) => coachSport.sport)?.[0]?.sport ||
    defaultSport;

  /**
   * @todo Handle multiple sports and certifications. Would need new UI.
   */
  const sportCertificationLogo = isProfessionalRacquetRegistryActive
    ? currentSport === SportsEnum.Pickleball
      ? 'ppr-vertical.png'
      : currentSport === SportsEnum.Tennis
      ? 'ptr-vertical.png'
      : currentSport === SportsEnum.Padel
      ? 'pcr-vertical.png'
      : null
    : null;

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full rounded-lg transition-shadow hover:shadow-[0px_6px_30px_0px_rgba(0,0,0,0.24)] hover:ring-1 hover:ring-color-border-hover-darkmode hover:ring-offset-8 hover:ring-offset-color-bg-lightmode-primary dark:hover:ring-offset-color-bg-darkmode-primary"
    >
      {sportCertificationLogo && (
        <div className="absolute right-2 top-0 z-[1] drop-shadow-[0px_-14px_20px_rgba(0,0,0,0.40)]">
          <img
            src={`/images/coaches/${sportCertificationLogo}`}
            alt="certification-logo"
            className="h-12 w-auto"
          />
        </div>
      )}

      {isPciMember && (
        <div
          className={`absolute top-0 z-[1] drop-shadow-[0px_-14px_20px_rgba(0,0,0,0.40)] ${
            sportCertificationLogo ? 'right-12' : 'right-2'
          }`}
        >
          <img src="/images/coaches/pci-badge.png" alt="PCI Member" className="h-12 w-auto" />
        </div>
      )}

      {isVairPro && (
        <div
          className={`absolute top-0 z-[1] drop-shadow-[0px_-14px_20px_rgba(0,0,0,0.40)] ${
            sportCertificationLogo && isPciMember
              ? 'right-[88px]'
              : sportCertificationLogo || isPciMember
              ? 'right-12'
              : 'right-2'
          }`}
        >
          <img src="/images/coaches/vair-pro.png" alt="VAIR Pro" className="h-12 w-auto" />
        </div>
      )}

      <div className="relative">
        {isInstantBookEligible && (
          <div className="absolute bottom-0 right-0 z-[1] flex items-center gap-1  rounded-br-lg rounded-tl-lg bg-color-bg-lightmode-invert px-2 py-1 shadow-sm dark:bg-color-bg-darkmode-invert">
            <BoltIcon className="h-3 w-3" />
            <span className="typography-product-chips-filters text-color-text-lightmode-invert dark:text-color-text-darkmode-invert">
              Instant book
            </span>
          </div>
        )}
        <NextImage
          src={photoUrl || EMPTY_COACH_AVATAR_SRC}
          alt={fullName}
          className={classNames(
            'aspect-square w-full rounded-lg border border-color-border-input-lightmode object-cover object-center dark:border-color-border-input-darkmode',
            isBookButton && 'relative h-48',
          )}
          loading="lazy"
          height={isBookButton ? 192 : 326}
          width={isBookButton ? 302 : 326}
        />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="typography-product-card-title min-w-0 flex-1 truncate text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {fullName}
        </div>
        {currentSport && (
          <SportBadge
            sport={currentSport as SportsEnum}
            size="xs"
            variant="neutral"
            className="shrink-0"
          />
        )}
      </div>
      {!!price && (
        <div className="mt-1 flex items-center gap-ds-xs lg:gap-2 flex-wrap">
          <Price className="h-4 w-4 text-color-bg-lightmode-icon max-lg:h-3.5 max-lg:w-3.5 dark:text-color-bg-darkmode-icon" />
          <span className="typography-product-text-card text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            Private lesson
          </span>
          <span className="typography-product-text-card text-color-text-lightmode-tertiary">&bull;</span>
          <span className="typography-product-text-card text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {convertUnitPriceToFormattedPrice(price, currency).priceDisplay}
            {isBookButton && '+'}
          </span>
        </div>
      )}
      {!!distance && (
        <div className="mt-1 flex items-center gap-ds-xs lg:gap-2 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          <Location className="h-4 w-4 text-color-bg-lightmode-icon max-lg:h-3.5 max-lg:w-3.5 dark:text-color-bg-darkmode-icon" />
          <span className="typography-product-text-card">
            {pluralize({
              count: Math.round(distance),
              singular: 'mile',
              plural: 'miles',
            })}
          </span>
        </div>
      )}
      {!!lessonGiven && (
        <div className="mt-1 flex items-center gap-ds-xs lg:gap-2">
          <Racket className="h-4 w-4 text-color-bg-lightmode-icon max-lg:h-3.5 max-lg:w-3.5 dark:text-color-bg-darkmode-icon" />
          <span className="typography-product-text-card text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {pluralize({
              count: lessonGiven,
              singular: 'Lesson given',
              plural: 'Lessons given',
              hideCount: true,
            })}
          </span>
          <span className="typography-product-text-card text-color-text-lightmode-tertiary">&bull;</span>
          <span className="typography-product-text-card text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {lessonGiven}
          </span>
        </div>
      )}

      {reviews && (
        <div className="mt-1 flex items-center gap-ds-xs lg:gap-2">
          <Star className="h-4 w-4 text-color-bg-lightmode-icon max-lg:h-3.5 max-lg:w-3.5 dark:text-color-bg-darkmode-icon" />
          <span className="typography-product-text-card text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {reviews?.averageRating?.toFixed(1)} (
            {pluralize({
              count: reviews?.totalReviews || 0,
              singular: 'review',
              plural: 'reviews',
            })}
            )
          </span>
        </div>
      )}
      <div className="flex justify-between gap-2">
        {isNotNullOrUndefined(followersCount) && (
          <div className="mt-1 flex items-center gap-ds-xs lg:gap-2 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            <Players className="h-4 w-4 text-color-bg-lightmode-icon max-lg:h-3.5 max-lg:w-3.5 dark:text-color-bg-darkmode-icon" />
            <span className="typography-product-text-card">
              {pluralize({
                count: followersCount,
                singular: 'follower',
                plural: 'followers',
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default function ExploreLessonCard({ isLink = true, impressionSource, ...props }: Props) {
  const cardRef = React.useRef<HTMLDivElement>(null);
  useEngagementImpression(cardRef, {
    entityType: 'COACH_PROFILE',
    entityId: props.id,
    source: impressionSource ?? 'BROWSE',
    disabled: !impressionSource,
  });

  if (isLink) {
    return (
      <Link href={withEngagementSource(getCoachPageUrl(props.slug), 'BROWSE')} className="w-full">
        <Content containerRef={cardRef} {...props} />
      </Link>
    );
  }

  return <Content containerRef={cardRef} {...props} />;
}
